import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { usersApi } from '../services/users';
import { shopsApi } from '../services/shops';
import { shopCategoriesApi } from '../services/shopCategories';
import { productCategoriesApi } from '../services/productCategories';
import { productsApi } from '../services/products';
import { companiesApi } from '../services/companies';
import { authApi } from '../services/auth';

interface User {
  _id: string;
  username: string;
  email: string;
  role: string;
  shop_id?: string;
  mobile_number: string;
  is_active: boolean;
  is_blocked: boolean;
  created_at: string;
  updated_at: string;
}

interface AuthState {
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
}

// Clear all stored data
const clearStoredData = () => {
  localStorage.clear();
  sessionStorage.clear();
  // Clear any cookies
  document.cookie.split(';').forEach(cookie => {
    document.cookie = cookie
      .replace(/^ +/, '')
      .replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
  });
};

// Load initial state from localStorage
const loadState = (): AuthState => {
  try {
    const serializedToken = localStorage.getItem('token');
    const serializedUser = localStorage.getItem('user');
    
    if (!serializedToken || !serializedUser) {
      clearStoredData();
      return {
        token: null,
        user: null,
        isAuthenticated: false
      };
    }

    // Parse and validate user data
    const user = JSON.parse(serializedUser);
    if (!user._id || !user.username || !user.role) {
      clearStoredData();
      return {
        token: null,
        user: null,
        isAuthenticated: false
      };
    }

    return {
      token: serializedToken,
      user,
      isAuthenticated: true
    };
  } catch (err) {
    clearStoredData();
    return {
      token: null,
      user: null,
      isAuthenticated: false
    };
  }
};

const initialState: AuthState = loadState();

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (state, action: PayloadAction<{ token?: string; user?: User }>) => {
      const { token, user } = action.payload;
      
      if (token && user) {
        // Clear any existing data first
        clearStoredData();
        
        // Reset all API states before setting new credentials
        usersApi.util.resetApiState();
        shopsApi.util.resetApiState();
        shopCategoriesApi.util.resetApiState();
        productCategoriesApi.util.resetApiState();
        productsApi.util.resetApiState();
        companiesApi.util.resetApiState();
        
        // Set new state
        state.token = token;
        state.user = user;
        state.isAuthenticated = true;
        
        // Save to localStorage
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
      } else if (!token && !user) {
        // If no credentials provided, clear everything
        clearStoredData();
        state.token = null;
        state.user = null;
        state.isAuthenticated = false;
      }
    },
    logout: (state) => {
      // Reset all API states - this is important to clear cached data
      usersApi.util.resetApiState();
      shopsApi.util.resetApiState();
      shopCategoriesApi.util.resetApiState();
      productCategoriesApi.util.resetApiState();
      productsApi.util.resetApiState();
      companiesApi.util.resetApiState();
      authApi.util.resetApiState();
      
      // Clear state
      state.token = null;
      state.user = null;
      state.isAuthenticated = false;
      
      // Clear all stored data
      clearStoredData();
    },
  },
});

export const { setCredentials, logout } = authSlice.actions;
export default authSlice.reducer;